import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import Collaboration from '@tiptap/extension-collaboration';
import type * as Y from 'yjs';

type CollaborativeSitrepEditorProps = {
  ydoc: Y.Doc;
  initialHtml: string;
  onHtmlChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  minHeight?: string;
  className?: string;
};

/**
 * Wait briefly for peer yjs-sync before seeding from REST HTML (avoids
 * duplicate inserts).
 */
const HYDRATE_WAIT_MS = 400;

/**
 * TipTap editor bound to a shared Y.Doc for live section collaboration.
 * Hydrates from HTML once when the Y.Doc fragment is still empty after a short
 * sync window.
 */
export const CollaborativeSitrepEditor: React.FC<
  CollaborativeSitrepEditorProps
> = ({
  ydoc,
  initialHtml,
  onHtmlChange,
  placeholder = 'Enter section content...',
  disabled = false,
  minHeight = '120px',
  className = '',
}) => {
  const hydratedRef = useRef(false);
  const onHtmlChangeRef = useRef(onHtmlChange);
  onHtmlChangeRef.current = onHtmlChange;
  // Capture seed HTML once — empty onUpdate from TipTap must not overwrite it
  // before the hydrate timer runs (that was wiping saved section text).
  const seedHtmlRef = useRef(initialHtml || '');
  if (!seedHtmlRef.current?.trim() && initialHtml?.trim()) {
    seedHtmlRef.current = initialHtml;
  }

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          heading: { levels: [1, 2, 3] },
          // Collaboration provides its own undo stack via Yjs.
          undoRedo: false,
          link: false,
          underline: false,
        }),
        Underline,
        Placeholder.configure({ placeholder }),
        Collaboration.configure({ document: ydoc }),
      ],
      editable: !disabled,
      editorProps: {
        attributes: {
          class: 'tiptap-editor__content',
          style: `min-height: ${minHeight}`,
        },
      },
      onUpdate: ({ editor: ed }) => {
        // Suppress pre-hydrate updates so empty `<p></p>` does not replace
        // saved section HTML in parent state / re-trigger hydrate with empty seed.
        if (!hydratedRef.current) return;
        onHtmlChangeRef.current(ed.getHTML());
      },
    },
    [ydoc]
  );

  useEffect(() => {
    if (!editor || hydratedRef.current) return;

    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled || hydratedRef.current) return;
      const fragment = ydoc.getXmlFragment('default');
      const seed = seedHtmlRef.current || '';
      const willSet = fragment.length === 0 && !!seed.trim();
      if (willSet) {
        // emitUpdate:true so Collaboration writes into the Y.Doc fragment.
        editor.commands.setContent(seed, { emitUpdate: true });
      }
      hydratedRef.current = true;
      onHtmlChangeRef.current(editor.getHTML());
    }, HYDRATE_WAIT_MS);

    const onDocUpdate = () => {
      if (cancelled || hydratedRef.current) return;
      const fragment = ydoc.getXmlFragment('default');
      // Only treat peer-populated content as hydrated; ignore empty editor init.
      if (
        fragment.length > 0 &&
        editor.getHTML().trim() &&
        editor.getHTML() !== '<p></p>'
      ) {
        hydratedRef.current = true;
        clearTimeout(timer);
        onHtmlChangeRef.current(editor.getHTML());
      }
    };
    ydoc.on('update', onDocUpdate);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      ydoc.off('update', onDocUpdate);
    };
    // Seed is captured in seedHtmlRef; do not re-run when parent content flips to empty.
  }, [editor, ydoc]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor) return null;

  return (
    <div className={`tiptap-editor ${className}`}>
      <EditorContent editor={editor} />
    </div>
  );
};
